import { Injectable } from "@angular/core";
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { Observable, forkJoin, of } from "rxjs";
import { map, catchError, switchMap } from "rxjs/operators";
import { AzureSearchService, AzureSearchRequest } from "../../../azure-search/azure-search";
import { BaseResolverService, AuthenticationService, ErrorsHandler } from "../../../core/core";
import { ToastrService } from "ngx-toastr";
import { RentalViewModel } from "../models/rental-view.model";
import { RentalRequest } from "../models/rental-request.model";
import { TransportationRequest } from "../../../transportation/transportation";
import { RentalItemStateService } from "./rental-item-state.service";
import { RentalItemStateSearchModel } from "../models/rental-item-state-search.model";
import { NgProgress } from "ngx-progressbar";
import { RentalRequestItem } from "../models/rental-request-item.model";
import { User } from "oidc-client";
import { RentalItemStageName, RentalItemState } from "../models/rental-item-state.model";
import { CartService } from "../../../cart/cart";
import { RentalRequestService } from "./rental-request.service";
import { Activity, ActivityService } from "../../../activity/activity";

@Injectable({
  providedIn: "root",
})
export class RentalResolverService extends BaseResolverService implements Resolve<any> {
  user: User;

  constructor(
    errorHandler: ErrorsHandler,
    toastrService: ToastrService,
    progress: NgProgress,
    private azureSearchService: AzureSearchService,
    private rentalItemStateService: RentalItemStateService,
    private authenticationService: AuthenticationService,
    private cartService: CartService,
    private rentalRequestService: RentalRequestService,
    private activityService: ActivityService,
  ) {
    super(errorHandler, toastrService, progress);
    this.user = this.authenticationService.getUserNonAsync();
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<RentalViewModel> | Observable<never> {
    this.startProgressBar();
    const rentalRequestId = route.paramMap.get("id");
    const transpoRequest = new AzureSearchRequest();
    transpoRequest.filter = `rentalRequestId eq '${rentalRequestId}'`;

    // get the rental item states for all rental items
    const rentalItemStateSearch = new RentalItemStateSearchModel();
    rentalItemStateSearch.pageSize = 1000;
    rentalItemStateSearch.rentalRequestIds = [rentalRequestId];
    rentalItemStateSearch.partitionKey = this.user.profile.account_id;

    return forkJoin([
      this.azureSearchService.getOne<RentalRequest>(rentalRequestId, "rental-requests"),
      this.azureSearchService.search<TransportationRequest>(transpoRequest, "transportation-request"),
      this.rentalItemStateService.getAll(rentalItemStateSearch),
      this.activityService.getAllActivitiesByRentalRequestId(rentalRequestId, "Renter"),
    ]).pipe(
      switchMap(responses => {
        const rentalRequest = responses[0];
        const rentalItemStates = responses[2].results;
        const returnTranspoRequestsIds = [] as string[];
        const activities = responses[3];

        const cartRequest$ = this.cartService.get(rentalRequest.cartId);
        if (rentalRequest.status === "Pending" || rentalRequest.status === "Canceled") {
          return forkJoin([of(responses[0] as RentalRequest), of([]), of([]), of({}), cartRequest$, of(activities)]);
        }

        if (rentalItemStates.length === 0) {
          throw new Error("unable to find rental item states. most likely the account is not verified.");
        }
        // grab all the rental item stages and flatten them into a single array of rental item stages
        const allRentalItemStages = rentalItemStates.map(r => r.stages).reduce((a, b) => a.concat(b));

        // go through each rental item stage
        for (const stage of allRentalItemStages) {
          // check for a fulfillment request
          const assocFulfillment = stage.associatedDocuments["FulfillmentRequest"];
          if (assocFulfillment && !returnTranspoRequestsIds.includes(assocFulfillment)) {
            returnTranspoRequestsIds.push(assocFulfillment);
          }
        }

        // if we have any fulfillmentrequests to query for
        let transpoRequestsForReturn$ = null;
        if (returnTranspoRequestsIds.length) {
          const secondTranspoRequest = new AzureSearchRequest();
          secondTranspoRequest.filter = `search.in(fulfillmentRequestId, '${returnTranspoRequestsIds.join(",")}')`;
          transpoRequestsForReturn$ = this.azureSearchService.search<TransportationRequest>(secondTranspoRequest, "transportation-request");
        }

        // ugly
        return forkJoin([
          of(responses[0] as RentalRequest),
          of(responses[1]["value"] as TransportationRequest[]),
          of(rentalItemStates as RentalItemState[]),
          transpoRequestsForReturn$,
          cartRequest$,
          of(activities),
        ]);
      }),
      map(responses => {
        const rental = responses[0] as RentalRequest;
        const transpoRequests = responses[1] as TransportationRequest[];
        const rentalItemStates = responses[2] as RentalItemState[];
        const transpoRequestsForReturn = responses[3]["value"] as TransportationRequest[];
        const cart = responses[4];
        const activities = responses[5] as Activity[];

        rental.items.forEach((rentalItem: RentalRequestItem) => {
          // rentalItem.cartItem = cart.items.find(d => d.inventoryItem.id == rentalItem.inventoryId);
          if (rentalItem.cartItem) {
            // there was an issue with dynamic adding of order items and sometimes there might not be a cart item since we had a bug
            rentalItem.rentalTerm = this.cartService.isWeeklyOrMonthly(rentalItem.cartItem.duration);
            rentalItem.chargesDropdownItems = this.rentalRequestService.setupChargesFromCartItem(rentalItem.cartItem);
          } else {
            rentalItem.cartItem = {} as any;
          }

          rentalItem.activities = activities.filter(d => d.inventoryItemId == rentalItem.inventoryId);
          const rentalItemState = rentalItemStates.find(d => d.inventoryItem.id == rentalItem.inventoryId) as RentalItemState;
          if (rentalItemState) {
            rentalItem.rentalItemState = rentalItemState;
          }

          rentalItem.statusData = this.rentalItemStateService.getRentalItemStatusData(rentalItem, true);
        });

        if (rental.status === "Pending" || rental.status === "Canceled") {
          this.finishProgressBar();
          return { rental, cart } as RentalViewModel;
        }

        if (transpoRequests.length === 0) {
          throw new Error(`Unable to find transportation request with rental id: ${rentalRequestId}`);
        }

        rental.items.forEach((rentalItem: RentalRequestItem) => {
          // find the corresponding rental item state for this rental item
          const rentalItemState = rentalItemStates.find(d => d.inventoryItem.id == rentalItem.inventoryId) as RentalItemState;
          if (!rentalItemState) {
            return;
          }

          // go through all the transpo requests and find the corrersponding load
          for (const transpoRequest of transpoRequests) {
            // map the status of the corresponding load to each order item. this is needed for the icon progress bar.
            const load = transpoRequest.loads.find(d => d.inventoryIds.includes(rentalItem.inventoryId));
            if (load != null) {
              rentalItemState.transportationLoad = load;
              break;
            }
          }

          if (rentalItemState.transportationLoad === null) {
            throw new Error(`Unable to find load with inventory id: ${rentalItem.inventoryId}`);
          }

          // check if the rental item state needs to map the return transpo load
          const returnStage = rentalItemState.stages.find(d => d.name === RentalItemStageName.ReturnFulfillment && d.associatedDocuments["FulfillmentRequest"]);
          if (transpoRequestsForReturn.length && returnStage) {
            const correspondingTranspoRequest = transpoRequestsForReturn.find(
              d => d.fulfillmentRequestId == returnStage.associatedDocuments["FulfillmentRequest"],
            );

            if (!correspondingTranspoRequest) {
              throw new Error(
                `Unable to find a corresponding return transportation load for fulfillment id ${returnStage.associatedDocuments["FulfillmentRequest"]}`,
              );
            }

            rentalItemState.returnTransportationLoad = correspondingTranspoRequest.loads.find(d => d.inventoryIds.includes(rentalItem.inventoryId));
          }
          // attach the rentalitemstate
          rentalItem.rentalItemState = rentalItemState;
        });

        this.finishProgressBar();
        return {
          rental,
          cart,
        } as RentalViewModel;
      }),
      catchError(e => {
        return this.logError(e);
      }),
    );
  }
}
